import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { GetTimeZones } from 'core/API/workplaces';
import { getDays } from 'core/API/organizationSettings';
import styles from './ShowWorkplaceSetting.module.scss';
import { TimeZone } from 'types';
import { Days } from 'types/Organization';
import { CircularProgress } from '@mui/material';

interface ShowWorkplaceSettingProps {
    workplace: any;
}

const ShowWorkplaceSetting: React.FC<ShowWorkplaceSettingProps> = ({ workplace }) => {
    const [timeZones, setTimeZones] = useState<TimeZone[]>([]);
    const [days, setDays] = useState<Days[]>([]);

    // Fetch time zones and days on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [zones, daysData] = await Promise.all([GetTimeZones(), getDays()]);
                setTimeZones(zones || []);
                // Sort days by ascending position (numeric)
                const sortedDays = (daysData || []).slice().sort((a: Days, b: Days) => {
                    const ai = Number(a.position ?? 0);
                    const bi = Number(b.position ?? 0);
                    return ai - bi;
                });
                setDays(sortedDays);
            } catch (error) {
                console.error('Failed to fetch time zones or days:', error);
            }
        };
        fetchData();
    }, []);


    const orderWorkingHours = (hours: any[]) => {
        if (!Array.isArray(hours) || hours.length === 0) return hours;
        // Build an ordered list of day identifiers from the days API (ascending position)
        const daysOrder = (days && days.length > 0)
            ? days.map(d => d['@id'] || d.label || String(d.id))
            : ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

        const getHourDayId = (hour: any) => {
            if (!hour) return '';
            const day = hour.day;
            if (!day) return '';
            if (typeof day === 'string') return day; // likely an @id
            if (day['@id']) return day['@id'];
            if (day.id !== undefined && days && days.length > 0) {
                // try to find matching day by numeric id and return its @id if available
                const match = days.find(d => Number(d.id) === Number(day.id));
                if (match) return match['@id'] || match.label;
            }
            return day.label || day.name || String(day);
        };

        const indexed = hours.map(h => ({ h, id: getHourDayId(h) }));
        return indexed.sort((a, b) => {
            const ai = daysOrder.indexOf(a.id);
            const bi = daysOrder.indexOf(b.id);
            const aIdx = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
            const bIdx = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
            return aIdx - bIdx;
        }).map(x => x.h);
    };

    // Normalize various time formats to 12-hour clock (hh:mm AM/PM) for consistent display
    const formatTime = (val: any): string => {
        if (val === null || val === undefined || val === '') return 'N/A';
        if (val === 'Closed') return 'Closed';

        // If object with nested time field, recursively try candidates
        if (typeof val === 'object' && !(val instanceof Date)) {
            const candidates = [val.open_at, val.openAt, val.openAtTime, val.openAtString, val.open, val.value, val.close_at, val.closeAt, val.closeAtTime, val.closeAtString, val.close];
            for (const c of candidates) {
                if (c) return formatTime(c);
            }
            return 'N/A';
        }

        // Date instance -> format
        if (val instanceof Date) {
            return dayjs(val).format('hh:mm A');
        }

        if (typeof val === 'string') {
            const s = val.trim();
            if (s === '') return 'N/A';

            // If it's an ISO datetime or parseable by Date/dayjs
            const parsed = dayjs(s);
            if (parsed.isValid()) {
                return parsed.format('hh:mm A');
            }

            // If matches HH:mm or HH:mm:ss manually
            const m = s.match(/^(\d{1,2}):(\d{2})(:(\d{2}))?$/);
            if (m) {
                const hh = Number(m[1]);
                const mm = Number(m[2]);
                const ss = Number(m[4] || '0');
                const d = dayjs().hour(hh).minute(mm).second(ss);
                return d.format('hh:mm A');
            }

            // Fallback to raw string
            return s;
        }

        return 'N/A';
    };

    if (!workplace) {
        return <div className={styles.loading}><CircularProgress size={20} color="inherit" /></div>;
    }

    return (
        <div className={styles.showWorkplaceSetting}>
            <h3 className={styles.title}>{workplace.name}</h3>
            <div className={styles.formContainer}>

                {/* ID */}
                <div className={styles.formField}>
                    <label className={styles.label}>Workplace Unit Number</label>
                    <input
                        type="text"
                        value={workplace.workplaceUnitNumber || workplace.identifier || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Name */}
                <div className={styles.formField}>
                    <label className={styles.label}>Name</label>
                    <input
                        type="text"
                        value={workplace.name}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Street Address */}
                <div className={styles.formField}>
                    <label className={styles.label}>Street Address</label>
                    <input
                        type="text"
                        value={workplace.streetAddress || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* City */}
                <div className={styles.formField}>
                    <label className={styles.label}>City</label>
                    <input
                        type="text"
                        value={workplace.city || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* State */}
                <div className={styles.formField}>
                    <label className={styles.label}>State</label>
                    <input
                        type="text"
                        value={workplace.state || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Postal Code */}
                <div className={styles.formField}>
                    <label className={styles.label}>Postal Code</label>
                    <input
                        type="text"
                        value={workplace.postcode || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Country */}
                <div className={styles.formField}>
                    <label className={styles.label}>Country</label>
                    <input
                        type="text"
                        value={workplace.country || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Time Zone */}
                <div className={styles.formField}>
                    <label className={styles.label}>Time Zone</label>
                    <input
                        type="text"
                        value={timeZones.find(tz => tz['@id'] === workplace.timeZone)?.label || 'N/A'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Working Hours */}
                <div className={styles.formField}>
                    <label className={styles.label}>Business Hours of Operation</label>
                    <div className={styles.workingHours}>
                        {((workplace.working_hours && workplace.working_hours.length > 0) || (workplace.workplaceWorkingHours && workplace.workplaceWorkingHours.length > 0)) ? (
                            <div className={styles.workingHoursGrid}>
                                {orderWorkingHours((workplace.working_hours && workplace.working_hours.length > 0) ? workplace.working_hours : workplace.workplaceWorkingHours).map((hour: any, index: number) => {
                                    const getDayLabel = (d: any) => {
                                        if (!d) return '';
                                        // If d is a string @id, try to find a matching day from fetched days
                                        if (typeof d === 'string') {
                                            const match = days.find(day => (day['@id'] || '').toString() === d.toString() || (day.label || '').toString().toLowerCase() === d.toString().toLowerCase());
                                            if (match) return match.label;
                                            const seg = d.split('/').pop();
                                            return seg || d;
                                        }
                                        // If day is an object, try numeric id lookup
                                        if (d['@id']) {
                                            const match = days.find(day => (day['@id'] || '').toString() === d['@id'].toString());
                                            if (match) return match.label;
                                        }
                                        if (d.id !== undefined) {
                                            const match = days.find(day => Number(day.id) === Number(d.id));
                                            if (match) return match.label;
                                        }
                                        return d.label || d.name || String(d);
                                    };

                                    const getOpenValue = (h: any) => {
                                        if (h.closed) return 'Closed';
                                        const candidate = h && (h.open_at ?? h.openAt ?? h.openAtTime ?? h.openAtString ?? h.open ?? null);
                                        return formatTime(candidate);
                                    };

                                    const getCloseValue = (h: any) => {
                                        if (h.closed) return 'Closed';
                                        const candidate = h && (h.close_at ?? h.closeAt ?? h.closeAtTime ?? h.closeAtString ?? h.close ?? null);
                                        return formatTime(candidate);
                                    };

                                    const dayLabel = getDayLabel(hour.day);
                                    const openVal = getOpenValue(hour);
                                    const closeVal = getCloseValue(hour);

                                    return (
                                        <div key={index} className={styles.workingHourRow}>
                                            <div className={styles.dayColumn}>
                                                <label className={styles.sublabel}>Day</label>
                                                <input
                                                    type="text"
                                                    value={dayLabel}
                                                    readOnly
                                                    className={styles.input}
                                                />
                                            </div>
                                            <div className={styles.timeColumn}>
                                                <label className={styles.sublabel}>Start Time</label>
                                                <input
                                                    type="text"
                                                    value={openVal}
                                                    readOnly
                                                    className={styles.input}
                                                />
                                            </div>
                                            <div className={styles.timeColumn}>
                                                <label className={styles.sublabel}>End Time</label>
                                                <input
                                                    type="text"
                                                    value={closeVal}
                                                    readOnly
                                                    className={styles.input}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles.noHours}>No working hours defined</div>
                        )}
                    </div>
                </div>

                {/* Workplace Status */}
                <div className={styles.formField}>
                    <label className={styles.label}>Workplace Status</label>
                    <input
                        type="text"
                        value={workplace.closed ? 'Closed' : 'Open'}
                        readOnly
                        className={styles.input}
                    />
                </div>

                {/* Open Date */}
                {workplace.openDate && (
                    <div className={styles.formField}>
                        <label className={styles.label}>Open Date</label>
                        <input
                            type="text"
                            value={workplace.openDate ? new Date(workplace.openDate).toLocaleDateString() : 'N/A'}
                            readOnly
                            className={styles.input}
                        />
                    </div>
                )}

                {/* Closed Date */}
                {workplace.closed && (
                    <div className={styles.formField}>
                        <label className={styles.label}>Closed Date</label>
                        <input
                            type="text"
                            value={workplace.closedDate ? new Date(workplace.closedDate).toLocaleDateString() : 'N/A'}
                            readOnly
                            className={styles.input}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ShowWorkplaceSetting;